import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { fetchG2GPriceCount, deleteG2GPrices } from '../../../services/g2gPriceService';

const TIME_PRESETS = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '3d', hours: 72 },
  { label: '7d', hours: 168 },
  { label: 'Custom', hours: null },
] as const;

interface Props {
  leagues: string[];
  game: string;
}

export function AdminDeleteSection({ leagues, game }: Props) {
  const [preset, setPreset] = useState<string>('24h');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
  const [preview, setPreview] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [result, setResult] = useState<{ deleted: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const getTimeRange = useCallback((): { start: string; end: string } => {
    const end = new Date();
    const p = TIME_PRESETS.find(t => t.label === preset);
    if (preset === 'Custom') {
      return { start: customStart, end: customEnd };
    }
    const start = new Date(end.getTime() - (p!.hours! * 3600_000));
    return { start: start.toISOString(), end: end.toISOString() };
  }, [preset, customStart, customEnd]);

  const loadPreview = useCallback(async () => {
    if (!selectedLeagues.length) { setPreview(null); return; }
    setPreviewLoading(true);
    setError(null);
    setResult(null);
    try {
      const { start, end } = getTimeRange();
      const ac = new AbortController();
      const data = await fetchG2GPriceCount({ start, end, game, leagues: selectedLeagues }, ac.signal);
      setPreview(data.count);
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      setError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [getTimeRange, game, selectedLeagues]);

  const handleDelete = async () => {
    setConfirmOpen(false);
    setDeleteLoading(true);
    setError(null);
    setResult(null);
    try {
      const { start, end } = getTimeRange();
      const ac = new AbortController();
      const data = await deleteG2GPrices({ start, end, game, leagues: selectedLeagues }, ac.signal);
      setResult({ deleted: data.deleted });
      setPreview(null);
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      setError('Delete failed: ' + (e instanceof Error ? e.message : 'unknown error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const canDelete = selectedLeagues.length > 0 && (preset !== 'Custom' || (customStart && customEnd));

  return (
    <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mt: 3 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <LockIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
        <Typography fontWeight={600} color="text.secondary">Admin Tools</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Time range */}
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary" width={80}>Time range</Typography>
            <ToggleButtonGroup
              value={preset}
              exclusive
              onChange={(_, v) => { if (v) setPreset(v); }}
              size="small"
            >
              {TIME_PRESETS.map(t => (
                <ToggleButton key={t.label} value={t.label}>{t.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {preset === 'Custom' && (
              <>
                <TextField
                  type="datetime-local"
                  size="small"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  sx={{ width: 220 }}
                />
                <Typography color="text.secondary">→</Typography>
                <TextField
                  type="datetime-local"
                  size="small"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  sx={{ width: 220 }}
                />
              </>
            )}
          </Box>

          {/* League multi-select */}
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <Typography variant="body2" color="text.secondary" width={80} mt={1}>Leagues</Typography>
            <Autocomplete
              multiple
              options={leagues}
              value={selectedLeagues}
              onChange={(_, v) => setSelectedLeagues(v)}
              size="small"
              sx={{ minWidth: 300, flex: 1 }}
              renderInput={(params) => <TextField {...params} placeholder="Select leagues" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} />
                ))
              }
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={previewLoading ? <CircularProgress size={14} /> : <SearchIcon />}
              onClick={loadPreview}
              disabled={!canDelete || previewLoading}
            >
              Preview
            </Button>
          </Box>

          {/* Preview result */}
          {preview !== null && (
            <Alert severity="info" sx={{ ml: '88px' }}>
              {preview === 0 ? 'No data rows match this filter' : `${preview.toLocaleString()} row${preview === 1 ? '' : 's'} would be deleted`}
            </Alert>
          )}

          {/* Actions */}
          {preview !== null && preview > 0 && (
            <Box display="flex" gap={1} alignItems="center" ml="88px">
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={deleteLoading ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={deleteLoading}
              >
                Delete {preview.toLocaleString()} Rows
              </Button>
            </Box>
          )}

          {/* Success / error */}
          {result && (
            <Alert severity="success" sx={{ ml: '88px' }}>Successfully deleted {result.deleted.toLocaleString()} rows.</Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ ml: '88px' }}>{error}</Alert>
          )}
        </Box>
      </AccordionDetails>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete{' '}
            <strong>{preview?.toLocaleString() ?? 'the selected'}</strong>{' '}
            G2G price snapshots for{' '}
            <strong>{selectedLeagues.join(', ')}</strong>{' '}
            between{' '}
            <strong>{new Date(getTimeRange().start).toLocaleString()}</strong> and{' '}
            <strong>{new Date(getTimeRange().end).toLocaleString()}</strong>.
          </Typography>
          <Typography color="error" mt={1} fontWeight={600}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting…' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}